import {
  DeviceTabletCamera,
  GearSix,
  Lightning,
  Link,
  Note,
} from '@phosphor-icons/react'
import usePermissions from 'hooks/usePermissions'
import * as React from 'react'
import { Helmet } from 'react-helmet'
import { Redirect } from 'react-router-dom'
import Title from 'ui/atoms/Title'

import useIntegration from '@/hooks/useIntegration'

import { useAuthContext } from '../auth/authContext'
import Loading from '../components/Loading'
import Ui from '../ui'
import { ZentakeContext } from '../utils/ZentakeContext'
import SettingsContent from './Settings/SettingsContent'

function Settings({ history }) {
  const { loading, user } = useAuthContext()
  const {
    canViewIntegrations,
    canViewGeneralSetting,
    canViewAutomation,
    canCreateClientProfile,
  } = usePermissions()
  const { updateUserDetails } = React.useContext(ZentakeContext)
  const { integration } = useIntegration()

  const settingsTabs = React.useMemo(() => {
    const tabs = []
    tabs.push({
      id: 'settings',
      title: (
        <>
          <GearSix className="text-base" /> Settings
        </>
      ),
    })

    if (!!integration && canViewAutomation) {
      tabs.push({
        id: 'automations',
        title: (
          <>
            <Lightning className="text-base" /> Automations
          </>
        ),
      })
    }

    tabs.push({
      id: 'tablets',
      title: (
        <>
          <DeviceTabletCamera className="rotate-180 text-base" /> Tablets
        </>
      ),
    })

    if (canCreateClientProfile) {
      tabs.push({
        id: 'custom-fields',
        title: (
          <>
            <Note className="text-base" /> Custom Fields
          </>
        ),
      })
    }

    if (canViewIntegrations) {
      tabs.push({
        id: 'integrations',
        title: (
          <>
            <Link className="text-base" /> Integrations
          </>
        ),
      })
    }

    return tabs
  }, [
    integration,
    canViewAutomation,
    canViewIntegrations,
    canCreateClientProfile,
  ])

  const [selectedTab, setSelectedTab] = React.useState(settingsTabs[0].id)
  const [isLoadingTeamConfig, setLoadingTeamConfig] = React.useState(false)

  React.useEffect(() => {
    async function updateData() {
      try {
        setLoadingTeamConfig(true)
        await updateUserDetails()
      } catch (error) {
      } finally {
        setLoadingTeamConfig(false)
      }
    }

    updateData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const selectTabByHash = React.useCallback(() => {
    const { hash } = history.location
    const id = hash.replace('#', '')
    setSelectedTab(id || settingsTabs[0].id)
  }, [history.location, settingsTabs])

  React.useEffect(() => {
    selectTabByHash()
  }, [selectTabByHash])

  const onSelectOption = React.useCallback(
    ({ id }) => {
      history.replace(`#${id}`)
    },
    [history]
  )

  if (!canViewGeneralSetting) {
    return <Redirect to="/" />
  }

  if (isLoadingTeamConfig || loading || !user) {
    return <Loading />
  }

  return (
    <>
      <Helmet>
        <title>Settings/Zentake</title>
      </Helmet>

      <div className="mx-auto mt-6 max-w-screen-xl pb-11 md:px-3">
        <div className="mx-auto max-w-screen-xl">
          <div className="mb-4 w-full lg:mb-0">
            <Title>Settings</Title>
          </div>
        </div>

        <div>
          <Ui.Tabs
            initialSelectedIndex={settingsTabs.indexOf(
              settingsTabs?.find((setting) => setting.id === selectedTab)
            )}
            onSelectOption={onSelectOption}
            options={settingsTabs}
          />
          <SettingsContent selectedTab={selectedTab} />
        </div>
      </div>
    </>
  )
}

export default Settings
